// src/components/ReceiptPanel.jsx
// Shows a receipt's line items and its stored image. Used by the Receipts page
// and the Expenses "view" modal. The image endpoint is authenticated, so the
// image is fetched as a blob with the bearer token.

import React, { useEffect, useState } from "react";
import { getReceipt, receiptImageUrl } from "../api/user";
import { tokenStore } from "../api/client";
import { Loading } from "./ui";
import { money, date } from "../lib/format";

export default function ReceiptPanel({ receiptId, highlightItemId, highlightAmount, highlightLabel }) {
  const [data, setData] = useState(null);
  const [imgSrc, setImgSrc] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let revoke;
    getReceipt(receiptId).then(setData).catch((e) => setErr(e.message));
    (async () => {
      try {
        const res = await fetch(receiptImageUrl(receiptId), {
          headers: { Authorization: `Bearer ${tokenStore.access}` },
        });
        if (res.ok) {
          const url = URL.createObjectURL(await res.blob());
          revoke = url;
          setImgSrc(url);
        }
      } catch { /* no image */ }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [receiptId]);

  if (err) return <p className="text-sm text-red-600">{err}</p>;
  if (!data) return <Loading label="Loading receipt" />;

  const items = data.items || [];
  // match the filed line: by id first, else fall back to a unique amount match
  let matchedId = null;
  if (highlightItemId && items.some((it) => String(it.id) === String(highlightItemId))) {
    matchedId = String(highlightItemId);
  } else if (highlightAmount != null) {
    const hits = items.filter((it) => Number(it.amount ?? it.price) === Number(highlightAmount));
    if (hits.length === 1) matchedId = String(hits[0].id);
  }
  if (!matchedId && highlightLabel) {
    const norm = (s) => (s || "").trim().toLowerCase();
    const hit = items.find((it) => norm(it.label) === norm(highlightLabel) || norm(it.raw_text) === norm(highlightLabel));
    if (hit) matchedId = String(hit.id);
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-semibold">{data.merchant || "Receipt"}</span>
          <span className="text-sm text-tb-muted">{date(data.receipt_date)}</span>
        </div>
        <div className="rounded-xl border border-tb-border dark:border-tb-d-border divide-y divide-tb-border dark:divide-tb-d-border overflow-hidden">
          {items.map((it) => {
            const on = matchedId && String(it.id) === matchedId;
            return (
              <div key={it.id}
                   className={`flex items-center justify-between gap-3 pr-3 py-2 text-sm border-l-4 pl-3 ${
                     on ? "border-tb-brand bg-tb-brand/10" : "border-transparent"}`}>
                <span className={`min-w-0 truncate ${on ? "text-tb-brand font-semibold" : ""}`}>
                  {it.label || it.raw_text || "Item"}
                </span>
                <span className={`tabular-nums shrink-0 ${on ? "text-tb-brand font-semibold" : "text-tb-muted"}`}>
                  {money(it.amount ?? it.price)}
                </span>
              </div>
            );
          })}
          {items.length === 0 && <p className="px-3 py-3 text-sm text-tb-muted">No line items.</p>}
        </div>
        {(highlightItemId || highlightAmount != null) && (
          matchedId
            ? <p className="mt-2 text-xs text-tb-muted">The highlighted line is the item filed as this expense.</p>
            : <p className="mt-2 text-xs text-tb-amber">
                Couldn't pinpoint the exact line on this receipt
                {highlightAmount != null ? ` (filed amount ${money(highlightAmount)})` : ""}
                {highlightItemId ? "" : " · no item link stored"}.
              </p>
        )}
        <p className="mt-3 text-right text-lg font-bold tabular-nums">{money(data.total)}</p>
      </div>
      <div>
        {imgSrc ? (
          <img src={imgSrc} alt="Receipt" className="w-full rounded-xl border border-tb-border dark:border-tb-d-border" />
        ) : (
          <div className="grid place-items-center h-48 rounded-xl bg-tb-bg-2 dark:bg-tb-d-inset text-sm text-tb-muted">
            No image available
          </div>
        )}
      </div>
    </div>
  );
}